import React, { useCallback, useContext } from 'react';

import { ResponseBlockType, SupportAction } from '../types';

export type ResponseActionValues = Record<string, string | string[] | null>;

export interface IOrbitResponseAction {
  blockId: string;
  blockType: ResponseBlockType;
  actionId: string;
  payload: SupportAction['payload'];
  /** Form field values, or the chosen metric ids, keyed as the block declared them. */
  values?: ResponseActionValues;
}

export type OrbitResponseActionHandler = (action: IOrbitResponseAction) => void;

const ignore: OrbitResponseActionHandler = () => undefined;

/**
 * The ONE handler every page response action goes through. Nothing provides it yet, so every
 * action is inert; the backend integration wraps the thread in `OrbitResponseActionProvider`.
 */
const OrbitResponseActionContext = React.createContext<OrbitResponseActionHandler>(ignore);

export const OrbitResponseActionProvider = OrbitResponseActionContext.Provider;

export type EmitResponseAction = (action: SupportAction, values?: ResponseActionValues) => void;

/** Binds the handler to one block, so a renderer only names the action it fires. */
export const useResponseAction = (block: {
  id: string;
  type: ResponseBlockType;
}): EmitResponseAction => {
  const handler = useContext(OrbitResponseActionContext);
  const { id, type } = block;
  return useCallback(
    (action, values) =>
      handler({
        blockId: id,
        blockType: type,
        actionId: action.id,
        payload: action.payload ?? null,
        values
      }),
    [handler, id, type]
  );
};
